import Subscription from "src/models/Subscription";
import { create } from "zustand";
import { getDefaultIsMultiplePOVState, normalizeIsMultiplePOVState } from "../../../src/shared/utils/isMultiplePOVState";
import Auth from "../Auth";
import { useProjectStore } from "./projectStore";

const initialState = {
  currentSeriesId: "",
  currentSeriesTitle: "",
  collapsed: false, // whether or not we expand the timeline component in the series folder view
  contents: [], // array of ALL the project in the series -> find project project.projectId === projectId (in the project store)
  allScenes: {}, // so we can preserve outline/document links
  // so we can view other project SB/SE values in the series (right now only Outline is hooked up)
  selectedProjects: {
    seed: null,
    genre: null,
    style: null,
    synopsis: null,
    outline: null, // { currently selected project }
    // characters and worldbuilding are shared across projects
  },
  seriesSectionRef: { current: null },
  selectedSeriesProjectId: "",

  // Feature flags
  hasAccessToPOVAndTenseBeta: false,
};

export const useSeriesStore = create((set, get) => {
  let context = 0;
  let refreshId = 0;
  const edits = new Map();
  const queues = new Map();
  const revision = (id) => edits.get(id)?.revision || 0;
  const mirror = (entry) => {
    if (entry?.povTense) useProjectStore.getState().applySeriesPOVTense(entry.projectId, entry.povTense, entry.isMultiplePOV);
  };

  // All contents readers share this rule, including save-failure recovery.
  const refresh = async (seriesId, save) => {
    const request = ++refreshId;
    const startedIn = context;
    const atStart = new Map([...edits].map(([id, edit]) => [id, { ...edit }]));
    const { series } = await Auth.fetchWithIdToken(`/api/series/${seriesId}`);
    if (!series || startedIn !== context || request !== refreshId) return;
    const contents = series.contents.map((entry) => {
      const id = entry.projectId;
      const previous = get().contents.find((item) => item.projectId === id);
      const changed = revision(id) !== (atStart.get(id)?.revision || 0);
      const pending = atStart.get(id)?.pending > 0;
      const obsoleteSave = save && (save.revisions.get(id) || 0) !== revision(id);
      // Successful PUTs already confirmed their partial fields. A GET must not
      // undo them, even if its server snapshot predates the completed write.
      const saved = save?.projectId === id && save.succeeded;
      if (previous && (changed || pending || obsoleteSave || saved)) {
        // A partial cache (including an undefined rollback field) must not hide
        // untouched settings returned by the server. Keep complete field metadata.
        const protectedFields = Object.fromEntries(Object.entries(previous.povTense || {}).filter(([, value]) => value !== undefined && value !== null));
        return { ...entry, povTense: { ...entry.povTense, ...protectedFields }, isMultiplePOV: previous.isMultiplePOV };
      }
      return entry;
    });
    set({ currentSeriesId: series.id, collapsed: series.collapseTimeline, contents, currentSeriesTitle: series.title });
    contents.forEach(mirror);
  };

  return {
    ...initialState,
    setCurrentSeriesId: (currentSeriesId) => {
      if (get().currentSeriesId !== currentSeriesId) context++;
      set({ currentSeriesId });
    },
    setContents: (contents) => {
      set({ contents });
    },

    resetSeries: () => {
      context++;
      refreshId++;
      edits.clear();
      set(initialState);
    },

    setCollapsed: async (collapsed) => {
      const seriesId = get().currentSeriesId;
      if (!seriesId) {
        throw new Error("Could not update series without series Id");
      }
      set({ collapsed });
      const series = await Auth.fetchWithIdToken(`/api/series/${seriesId}`, {
        method: "PUT",
        collapseTimeline: collapsed,
      });
    },

    setAllScenes: (projectId, scenes) => {
      set((state) => ({
        allScenes: {
          ...state.allScenes,
          [projectId]: scenes,
        },
      }));
    },

    getScenesByProjectId: (projectId) => {
      return get().allScenes[projectId] || [];
    },

    setSelectedProject: (type, project) => {
      set((state) => ({
        selectedProjects: {
          ...state.selectedProjects,
          [type]: project,
        },
      }));
    },

    getContentsBySeriesId: async (seriesId) => {
      if (get().currentSeriesId !== seriesId) context++;
      return refresh(seriesId);
    },

    setSeriesSectionRef: (ref) => {
      set((state) => ({
        seriesSectionRef: {
          current: ref,
        },
      }));
    },

    setSelectedSeriesProjectId: (selectedSeriesProjectId) => {
      set({ selectedSeriesProjectId });
    },

    getPOVTenseForSeriesProject: async (projectId) => {
      const contents = get().contents;
      let project = contents.find((c) => c.projectId === projectId);
      const startedIn = context;

      const userId = project?.userId;
      if (userId) {
        const subscription = await Subscription.fetchFromUserId(userId);
        if (subscription) {
          const { hasAccessToPOVAndTenseBeta } = subscription.getFeatureFlags();
          if (!hasAccessToPOVAndTenseBeta) {
            return null;
          }
        }
      }

      if (startedIn !== context) return null;
      project = get().contents.find((c) => c.projectId === projectId);
      return project
        ? {
            povTense: project.povTense,
            isMultiplePOV: normalizeIsMultiplePOVState(project.isMultiplePOV || getDefaultIsMultiplePOVState()),
          }
        : null;
    },

    // Include user inputs that have not reached the optimistic cache yet because
    // subscription access is still loading. Mode changes must capture these too.
    getIntendedPOVTenseForSeriesProject: (projectId) => {
      const entry = get().contents.find((item) => item.projectId === projectId);
      const povTense = { ...entry?.povTense };
      for (const intent of edits.get(projectId)?.intents?.values() || []) {
        if (intent.pov) povTense.pov = intent.pov;
        if (intent.tense) povTense.tense = intent.tense;
      }
      return povTense;
    },

    updatePOVTenseForSeriesProject: async (projectId, povTense, isMultiplePOV) => {
      const seriesId = get().currentSeriesId;
      const project = get().contents.find((c) => c.projectId === projectId);
      if (!seriesId || !project?.userId) return;

      const startedIn = context;
      const edit = edits.get(projectId) || { revision: 0, pending: 0, intents: new Map() };
      if (!edit.pending) {
        edit.confirmedPOVTense = project.povTense;
        edit.confirmedMode = project.isMultiplePOV;
      }
      edit.revision++;
      edit.pending++;
      edit.intents.set(edit.revision, povTense);
      edits.set(projectId, edit);
      const save = { projectId, revision: edit.revision, succeeded: false, revisions: new Map([...edits].map(([id, item]) => [id, item.revision])) };
      const finishProjectSave = useProjectStore.getState().beginSeriesPOVTenseSave(projectId);
      const normalizedMode = isMultiplePOV === undefined ? undefined : normalizeIsMultiplePOVState(isMultiplePOV);
      // Queue before any await, so subscription latency cannot reorder partial PUTs.
      const key = `${seriesId}/${projectId}`;
      const previous = queues.get(key) || Promise.resolve();
      let applied = false;
      const ready = Subscription.fetchFromUserId(project.userId).then((subscription) => {
        if (subscription && !subscription.getFeatureFlags().hasAccessToPOVAndTenseBeta) return null;
        if (startedIn !== context) return { subscription };
        const patch = {};
        for (const field of ["pov", "tense"]) {
          if (povTense[field] && (edit[`${field}Revision`] || 0) < save.revision) {
            patch[field] = povTense[field];
            edit[`${field}Revision`] = save.revision;
          }
        }
        const applyMode = normalizedMode !== undefined && (edit.modeRevision || 0) < save.revision;
        if (applyMode) edit.modeRevision = save.revision;
        set((state) => ({ contents: state.contents.map((entry) => entry.projectId !== projectId ? entry : {
          ...entry,
          povTense: { ...entry.povTense, ...patch },
          isMultiplePOV: applyMode ? normalizedMode : entry.isMultiplePOV,
        }) }));
        applied = true;
        mirror(get().contents.find((entry) => entry.projectId === projectId));
        return { subscription };
      });
      // Attach a rejection handler immediately while this save waits its turn.
      const prepared = ready.then((value) => ({ value }), (error) => ({ error }));
      const operation = previous.catch(() => {}).then(async () => {
        const { value, error } = await prepared;
        if (error) throw error;
        if (!value) return;
        const body = { method: "PUT", projectId, povTense, subscription: value.subscription };
        if (normalizedMode !== undefined) body.isMultiplePOV = normalizedMode;
        await Auth.fetchWithIdToken(`/api/series/${seriesId}/updatePOVTense`, body);
        save.succeeded = true;
        edit.confirmedPOVTense = {
          pov: povTense.pov || edit.confirmedPOVTense?.pov,
          tense: povTense.tense || edit.confirmedPOVTense?.tense,
        };
        if (normalizedMode !== undefined) edit.confirmedMode = normalizedMode;
      });
      queues.set(key, operation);
      let failure;
      try {
        await operation;
      } catch (error) {
        failure = error;
        if (applied && startedIn === context) {
          set((state) => ({ contents: state.contents.map((entry) => {
            if (entry.projectId !== projectId) return entry;
            const restored = { ...entry.povTense };
            for (const field of ["pov", "tense"]) {
              if (edit[`${field}Revision`] === save.revision) restored[field] = edit.confirmedPOVTense?.[field];
            }
            return { ...entry, povTense: restored, isMultiplePOV: edit.modeRevision === save.revision ? edit.confirmedMode : entry.isMultiplePOV };
          }) }));
          mirror(get().contents.find((entry) => entry.projectId === projectId));
        }
      } finally {
        edit.pending--;
        edit.intents.delete(save.revision);
        // Notify this book's consumers after removing the intent, even when access
        // failed before an optimistic update. Remounted controls must reconcile too.
        if (startedIn === context) {
          set((state) => ({ contents: state.contents.map((entry) => entry.projectId === projectId ? { ...entry } : entry) }));
        }
        finishProjectSave();
        if (queues.get(key) === operation) queues.delete(key);
      }
      if (applied && startedIn === context) {
        try {
          await refresh(seriesId, save);
        } catch (error) {
          failure ??= error;
        }
      }
      if (failure) throw failure;
    },
  };
});
